/* eslint-disable array-bracket-newline */
/**
 * A hook that is responsible for generating the user's signature so that he can log in
 */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSignTypedData } from 'wagmi';
import {
  Account,
  TonProofItemReplySuccess,
  toUserFriendlyAddress,
} from '@tonconnect/sdk';
import { actionAuth, actionSetAuth } from '../../actions/auth';
import { actionCreateNotification } from '../../actions/notifications';
import { getAuthFromLS } from '../../helpers/auth';

const days_14InSec = 1209600;
const DESCRIPTION = `Sign up to create a user session that will allow you to work with limit orders, price alerts, watchlist and other features of Kattana

Note: This permission does not apply to token management`;

const useSignAuthAction = () => {
  const dispatch = useDispatch();
  const ipRestricted = false;
  const [value, setValue] = useState<any>();

  const domain = {
    name: 'Kattana Auth',
    version: '1',
  };

  const types = {
    Auth: [
      { name: 'comment', type: 'string' },
      { name: 'network', type: 'string' },
      { name: 'wallet', type: 'address' },
      { name: 'device', type: 'string' },
      { name: 'expired_at', type: 'int64' },
      { name: 'ip_restricted', type: 'bool' },
    ],
  };

  const { data, error, isSuccess, isError, signTypedDataAsync, isLoading } =
    useSignTypedData({
      domain,
      types,
      primaryType: 'Auth',
    } as any);

  useEffect(() => {
    if (isError && error) {
      if ((error.message as string).includes('User denied message signature')) {
        dispatch(
          actionCreateNotification({
            kind: 'warning',
            message: 'User rejected signature action',
          })
        );
      } else {
        dispatch(
          actionCreateNotification({
            kind: 'error',
            message: error.message,
          })
        );
      }

      dispatch(actionSetAuth({ authFailed: true, loggedIn: false }));
    }
  }, [isError, error]);

  useEffect(() => {
    if (data && isSuccess) {
      if (value.network === 'TON') {
        const { address, proof, walletStateInit, publicKey } =
          data as unknown as Account & TonProofItemReplySuccess;
        dispatch(
          actionAuth({
            signature: proof.signature,
            payload: {
              ...value,
              wallet: toUserFriendlyAddress(address),
              timestamp: proof.timestamp,
              domain: proof.domain,
              tonProofPayload: proof.payload,
              tonProofStateInit: walletStateInit,
              tonPubKey: publicKey,
            },
          })
        );
      } else {
        dispatch(
          actionAuth({
            signature: data,
            payload: value,
          })
        );
      }
    }
  }, [isSuccess, data]);

  const chechAuth = useCallback(
    (network: string, wallet: string) => {
      getAuthFromLS(network, wallet);
      const auth = getAuthFromLS(network, wallet);
      if (auth) {
        dispatch(
          actionSetAuth({
            loggedIn: true,
            signature: auth.signature,
            expires_at: Number(auth.payload.expired_at),
            wallet: auth.payload.wallet,
            network: auth.payload.network,
            sessionId: auth.sessionId,
          })
        );
      }
      return !!auth;
    },
    [dispatch]
  );

  const onSignAuth = useCallback(
    async (network: string, wallet: string, device: string) => {
      if (isLoading) {
        console.log('provider doesn`t ready');
        return;
      }

      const expiredAt = Math.floor(new Date().getTime() / 1000) + days_14InSec;
      const message: any = {
        comment: DESCRIPTION,
        network: network,
        wallet: wallet,
        device: device,
        expired_at: expiredAt.toString(),
        ip_restricted: ipRestricted,
      };
      setValue(message);
      // eslint-disable-next-line no-debugger
      await signTypedDataAsync({ message: message });
    },
    [ipRestricted, isLoading, signTypedDataAsync]
  );

  return {
    chechAuth,
    onSignAuth,
    isLoading,
  };
};

export default useSignAuthAction;
